import {
  find, kebabCase, reduce, replace,
  round, toUpper,
} from 'lodash';
import { Settings } from 'luxon';
import { Currencies } from '@sda/enums';
import Strings from './data';

export const DEFAULT_CURRENCY = Currencies.USD;

export const DEFAULT_LOCALE = 'en_US';

/**
 * Gets the default locale from either luxon settings or from a hard-coded string for Canadian English
 * @return {string} locale
 */
export const getDefaultLocale = (): string => Settings.defaultLocale || DEFAULT_LOCALE;

/**
  * Converts underscored locales into BCP47 format used by Intl
  * @param  {string} locale - The locale to be formatted
  * @return {string} BCP47 formatted locale
  */
export const toBCP47Locale = (locale?: string): string => kebabCase(locale || getDefaultLocale());

/**
 * Returns a formatted money value in the specified currency
 * @param  {string|number} val
 * @param  {string} [currency]
 * @param  {string} [locale] - the user locale
 * @param  {number} [numDigits] - number of fraction digits to include
 * @return {string}
 */
export const formatTotalAsCurrency = (
  val: string | number,
  currency: string | Currencies = DEFAULT_CURRENCY,
  locale?: string,
  numDigits = 2,
): string => {
  let total = 0;

  if (val) {
    total = parseInt(`${val}`, 10) / 100;
  }

  const result = total.toLocaleString(toBCP47Locale(locale || getDefaultLocale()), {
    currency: toUpper(currency || DEFAULT_CURRENCY),
    style: 'currency',
    minimumFractionDigits: numDigits,
    maximumFractionDigits: numDigits,
  });

  if (result.substring(result.length - 3) === '.00') {
    return result.replace(/\.00/, '');
  }

  return result;
};

/**
 * Fetches a string for the specified language
 * @param  {string} key String key
 * @param  {object} replacement String value map of variable replacements
 * @param  {string} lang? String language
 * @return {string}
 */
export const getString = (key: string, replacement = {}, lang = process.env.SERVER_LANGUAGE): string => {
  const getLangField = (property) => {
    if (!property) {
      return '';
    }

    if (!Strings[lang]) {
      return property;
    }

    const buildValuesReturn = (valueObject, propArray) => {
      let newValueObject = valueObject;
      const nextProperty = propArray.shift();

      if (nextProperty && Object.prototype.hasOwnProperty.call(newValueObject, nextProperty)) {
        newValueObject = buildValuesReturn(newValueObject[nextProperty], propArray);
      }

      return newValueObject;
    };

    if (property.indexOf('.') > -1) {
      return buildValuesReturn(Strings[lang], property.split('.'));
    }

    return Strings[lang][property];
  };

  return reduce(
    replacement,
    (result, value, pattern) => replace(result, `{{${pattern}}}`, value),
    getLangField(key),
  );
};

/**
 * Renders the symbol for the given currency code
 * @param  {Currencies} currency
 * @param  {string} locale - the user locale
 * @return {string}
 */
export const getCurrencySymbol = (currency: Currencies = DEFAULT_CURRENCY, locale: string = getDefaultLocale()): string => {
  if (!currency) {
    return '$';
  }

  const formatter = new Intl.NumberFormat(toBCP47Locale(locale), { style: 'currency', currency });
  const parts = formatter.formatToParts(0);

  if (parts) {
    const currencyPart = find(parts, (part) => part.type === 'currency');

    if (currencyPart) {
      return currencyPart.value;
    }
  }

  return '$';
};

/**
 * Converts a decimal value to a percent
 * @param  {number} value [If between 0 and 1, convert to a percentage. Otherwise, return the number]
 * @return {string}
 */
export const convertToPercent = (value: number) => `${round(value < 1 ? value * 100 : value, 2)}%`;
